"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCsvInfoFromPath = exports.getCsvInfoFromString = void 0;
const papaparse_1 = __importDefault(require("papaparse"));
const type_schema_json_1 = __importDefault(require("@readcube/rcp-meta/type_schema.json"));
const specialLables_1 = require("./specialLables");
const headers_1 = __importDefault(require("./headers"));
const specialJanssenMappings_1 = require("./specialJanssenMappings");
const getCsvInfoFromString = async ({ csvString, customSchema, customFields, allowCopyrightStatus }) => {
    try {
        const rows = await getCsvRowsFromCsvString({ csvString });
        if (rows?.length < 2) {
            throw new Error("Missing row data in csv file.");
        }
        const rowEstimate = await getRowsEstimateFromCsvString({ csvString });
        return getCsvInfoAsResult({ rows, rowEstimate, customSchema, customFields, allowCopyrightStatus });
    }
    catch (error) {
        return { success: false, error };
    }
};
exports.getCsvInfoFromString = getCsvInfoFromString;
const getCsvInfoFromPath = async ({ csvPath, customSchema, customFields, allowCopyrightStatus }) => {
    try {
        const rows = await getCsvRowsFromPath({ csvPath });
        if (rows?.length < 1) {
            throw new Error("Missing row data in csv file.");
        }
        const rowEstimate = await getRowsEstimateFromPath({ csvPath });
        return getCsvInfoAsResult({ rows, rowEstimate, customSchema, customFields, allowCopyrightStatus });
    }
    catch (error) {
        return { success: false, error };
    }
};
exports.getCsvInfoFromPath = getCsvInfoFromPath;
const getCsvInfoAsResult = ({ rows, rowEstimate, customSchema, customFields, allowCopyrightStatus }) => {
    try {
        const csvInfo = getCsvInfo({ rows, rowEstimate, customSchema, customFields, allowCopyrightStatus });
        return { success: true, result: csvInfo };
    }
    catch (error) {
        return { success: false, error };
    }
};
const removeCustomStatusLabel = (labels) => {
    const { "Copyright status": _, ...labelsWithoutCopyrightStatus } = labels;
    return labelsWithoutCopyrightStatus;
};
const getCsvInfo = ({ rows, rowEstimate, customSchema, customFields, allowCopyrightStatus }) => {
    const specialLabelsOptionalCustomType = customSchema
        ? { ...specialLables_1.specialLabels, "Custom Type": "custom_type" }
        : { ...specialLables_1.specialLabels };
    const specialLabelsForThisImport = allowCopyrightStatus
        ? specialLabelsOptionalCustomType
        : removeCustomStatusLabel(specialLabelsOptionalCustomType);
    const csvColumnLabelPairs = Object
        .values(customSchema?.field_defs || type_schema_json_1.default.field_defs)
        .reduce((pairs, fd) => {
        if (fd?.input?.label && fd?.csv) {
            const csvColumnTitle = fd.csv.toLowerCase();
            if (!pairs[csvColumnTitle])
                pairs[csvColumnTitle] = fd.input.label;
        }
        return pairs;
    }, {});
    const [header, row, ...rest] = rows;
    const columnsToIgnore = Object
        .entries((0, headers_1.default)({ urls: { baseAppUrl: "", baseWebUrl: "" } }))
        .filter((entry) => {
        const headerConfig = entry[1];
        return headerConfig.isReadOnly;
    })
        .map((entry) => {
        const headerName = entry[0];
        return headerName.toLowerCase();
    });
    if (!allowCopyrightStatus)
        columnsToIgnore.push("copyright status");
    const columns = header
        .map((csvColumnTitle, index) => {
        const data = index < row.length
            ? row[index]
            : '';
        const lowercaseTitle = csvColumnTitle.toLowerCase();
        if (columnsToIgnore.includes(lowercaseTitle))
            return;
        const specialLabelsByCsvColumn = Object.fromEntries(Object.
            entries(specialLabelsForThisImport)
            .map(([k, v]) => [v.toLowerCase(), k]));
        let papersFieldDefaultLabel = csvColumnLabelPairs[lowercaseTitle]
            ?? specialLabelsByCsvColumn[lowercaseTitle];
        const customFieldMatchByField = customFields
            ?.find((customField) => customField.field === lowercaseTitle);
        if (customFieldMatchByField)
            return {
                key: csvColumnTitle,
                csvColumn: csvColumnTitle,
                customField: customFieldMatchByField.display,
                dataPreview: data,
            };
        if (!papersFieldDefaultLabel && customSchema) {
            const janssenMapping = specialJanssenMappings_1.specialJanssenMappings
                .find(sj => sj.label === lowercaseTitle);
            if (janssenMapping)
                papersFieldDefaultLabel = janssenMapping.label;
        }
        return {
            key: csvColumnTitle,
            csvColumn: csvColumnTitle,
            papersField: papersFieldDefaultLabel,
            dataPreview: data,
        };
    })
        .filter((f) => f);
    let uniqueFieldLabels = Object.keys(specialLabelsForThisImport);
    uniqueFieldLabels =
        Object
            .values(type_schema_json_1.default.field_defs)
            .reduce((unique, fd) => {
            if (fd?.input?.label) {
                if (!unique.map(l => l.toLowerCase()).includes(fd.input.label.toLowerCase()))
                    unique.push(fd.input.label);
            }
            return unique;
        }, uniqueFieldLabels);
    if (!uniqueFieldLabels.map(l => l.toLowerCase()).includes("Item ID".toLowerCase()))
        uniqueFieldLabels.unshift("Item ID");
    const customFieldLabels = customFields
        ?.map((customField) => customField.display)
        ?? [];
    uniqueFieldLabels.sort((a, b) => a.localeCompare(b));
    customFieldLabels.sort((a, b) => a.localeCompare(b));
    return { defaultMap: columns, rowEstimate, papersFields: uniqueFieldLabels, customFields: customFieldLabels };
};
const getCsvRowsFromCsvString = ({ csvString }) => {
    const parseResult = papaparse_1.default.parse(csvString, { preview: 2 });
    return parseResult.data;
};
const getCsvRowsFromPath = async ({ csvPath }) => {
    const csvString = await firstTwoLines({ path: csvPath });
    const parseResult = papaparse_1.default.parse(csvString, { preview: 2 });
    return parseResult.data;
};
const firstTwoLines = ({ path }) => new Promise((resolve, reject) => {
    const fs = require('fs');
    const readStream = fs.createReadStream(path);
    let acc = '';
    let pos = 0;
    let index;
    let newLineCount = 0;
    readStream
        .on('data', (chunk) => {
        index = chunk.indexOf('\n');
        acc += chunk;
        if (index === -1) {
            pos += chunk.length;
        }
        else {
            newLineCount++;
            if (newLineCount === 2) {
                pos += index;
                readStream.close();
            }
            else {
                pos += chunk.length;
            }
        }
    })
        .on('close', () => resolve(acc.slice(acc.charCodeAt(0) === 0xFEFF ? 1 : 0, pos)))
        .on('error', (err) => reject(err));
});
const getRowsEstimateFromCsvString = ({ csvString }) => csvString
    .split(/\r?\n/)
    .length;
const getRowsEstimateFromPath = ({ csvPath }) => new Promise((res, rej) => {
    let rowEst = 0;
    const fs = require('fs');
    const readStream = fs.createReadStream(csvPath);
    readStream.on('data', (chunk) => {
        for (let i = 0; i < chunk.length; i++) {
            if (chunk[i] == 10)
                rowEst++;
        }
    });
    readStream.on('end', () => res(rowEst));
    readStream.on('error', (err) => rej(err));
});
